import classNames from 'classnames';
import { ICON_CODEPOINTS } from '../types/iconCodepoints';
import { MaterialIconName } from '../types/materialIconName';

type IconProps = {
  size?: number;
  px?: number;
  'aria-label'?: string;
  className?: string;
  icon: MaterialIconName;
  onClick?: () => void;
};

export function MaterialIcon({
  size = 3,
  px,
  'aria-label': ariaLabel,
  className,
  icon,
  onClick,
}: IconProps): JSX.Element {
  // Whitelist icon → emit PUA codepoint (e.g. ""). The 'Material Symbols
  // Subset' @font-face has that glyph → only the ~6 KB subset font loads.
  // Unknown icon → emit the name as ASCII text (e.g. "exotic_icon"). The
  // subset has no ASCII glyphs, so the browser falls through to the
  // 'Material Symbols Outlined' family (full v338 font, ~311 KB), where
  // OpenType liga substitutes the name with the matching icon glyph.
  // See styles/MaterialSymbolsOutlined.subset.css for the family chain.
  const glyph = ICON_CODEPOINTS[icon] ?? icon;
  const fontSize = px ?? (size !== 3 ? `${size * 8}px` : undefined);
  const isInteractive = !!onClick;

  return (
    <span
      className={classNames(
        'material-symbols-outlined select-none overflow-hidden',
        className,
        isInteractive && 'cursor-pointer'
      )}
      role={isInteractive ? 'button' : 'img'}
      style={{ fontSize }}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : 'true'}
      onClick={onClick}
      onKeyDown={isInteractive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      } : undefined}
      tabIndex={isInteractive ? 0 : undefined}
    >
      {glyph}
    </span>
  );
}
